/**
 * Kirish sahifasi (ЧТЗ 4.1.6, 4.2.11.5).
 *
 * To'rt usul: login (e-pochta + parol, kerak bo'lsa ikki bosqichli tasdiqlash),
 * OneID orqali SSO, E-imzo (ERI kaliti, `EriLogin.tsx`) va Smart Forestry USB tokeni
 * (`UsbTokenLogin.tsx`). Tokenlar HttpOnly cookie'da qaytadi — bu yerda hech qanday
 * token saqlanmaydi.
 */
import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { ApiError } from "@/shared/api/client";
import { authApi } from "@/shared/api/endpoints";
import { IDLE_FLAG_KEY, homeRoute, useAuth } from "@/app/AuthContext";
import { useI18n, tr } from "@/shared/i18n";
import { LangSwitch, TextSizeButton, ThemeButton } from "@/shared/ui/Controls";
import { BrandMark, EImzoMark, Icon, OneIdMark } from "@/shared/ui/Icon";
import type { IconName } from "@/shared/ui/Icon";

import { EriLogin } from "./EriLogin";
import { UsbTokenLogin } from "./UsbTokenLogin";

type Method = "email" | "oneid" | "eimzo" | "token";
type Step = "credentials" | "mfa";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Kirishdan keyingi qaytish manzili (`?next=/xizmat/market`) — faqat sayt
 * ichidagi nisbiy yo'l qabul qilinadi (ochiq redirect zaifligi bo'lmasligi uchun).
 */
function safeNext(value: string | null): string {
  if (!value || !value.startsWith("/") || value.startsWith("//") || value.includes("\\")) return "";
  return value;
}

/** Kabinetga kirish usullari (tablar tartibi shu yerda). */
const METHOD_TABS: Array<{ method: Method; icon: IconName; label: string }> = [
  { method: "email", icon: "mail", label: "auth.tab.email" },
  { method: "oneid", icon: "user", label: "auth.tab.oneid" },
  { method: "eimzo", icon: "key", label: "auth.tab.eimzo" },
  { method: "token", icon: "usb", label: "auth.tab.token" },
];

export function LoginPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));

  const [method, setMethod] = useState<Method>(() => {
    const wanted = params.get("method");
    return wanted === "oneid" || wanted === "eimzo" || wanted === "token" ? wanted : "email";
  });
  const [step, setStep] = useState<Step>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(() => {
    try {
      if (sessionStorage.getItem(IDLE_FLAG_KEY)) {
        sessionStorage.removeItem(IDLE_FLAG_KEY);
        return "15 daqiqa harakatsizlik tufayli sessiya tugadi. Qayta kiring.";
      }
    } catch {
      /* ahamiyatsiz */
    }
    return "";
  });

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");

    if (step === "credentials") {
      if (!email.trim() || !password) return setError(t("auth.err.required"));
      if (!EMAIL_RE.test(email.trim())) return setError(t("auth.err.email"));
    } else if (otp.trim().length !== 6) {
      return setError(t("auth.err.otp"));
    }

    setBusy(true);
    try {
      const result = await authApi.login({
        email: email.trim().toLowerCase(),
        password,
        otp: step === "mfa" ? otp.trim() : undefined,
        trust_device: trustDevice,
      });
      setSession(result.user);
      /* Mijoz o'z kabinetiga, xodim esa ish kabinetiga tushadi. */
      const home = homeRoute(result.user);
      navigate(result.must_change_password ? `${home}?parol=almashtirish` : next || home, {
        replace: true,
      });
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.code === "mfa_required") {
          setStep("mfa");
          setNotice(t("auth.mfa.sub"));
        } else {
          setError(caught.isOffline ? t("auth.err.network") : caught.message);
        }
      } else {
        setError(t("auth.err.network"));
      }
    } finally {
      setBusy(false);
    }
  }

  async function startOneId() {
    setError("");
    setBusy(true);
    try {
      const { url } = await authApi.oneidAuthorize(next || "/kabinet");
      window.location.assign(url);
    } catch (caught) {
      setError(
        caught instanceof ApiError && !caught.isOffline ? caught.message : t("auth.err.network"),
      );
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <div className="auth-panel-top">
        <Link className="btn btn-ghost btn-sm" to="/">
          <Icon name="arrowRight" size={16} className="flip" />
          {t("auth.backToPortal")}
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <TextSizeButton variant="light" />
          <ThemeButton variant="light" />
          <LangSwitch variant="light" />
        </div>
      </div>

      {/* -------------------------------------------------------------- forma */}
      <section className="auth-panel">
        <div className="auth-form-wrap">
          <Link className="brand auth-brand" to="/">
            <BrandMark className="brand-mark" spin />
            <span className="brand-text">
              <b>{t("app.name")}</b>
              <span>{t("app.agency")}</span>
            </span>
          </Link>

          <div className="auth-tabs" role="tablist" aria-label={t("auth.title")}>
            {METHOD_TABS.map((tab) => (
              <button
                key={tab.method}
                type="button"
                role="tab"
                aria-selected={method === tab.method}
                onClick={() => {
                  setMethod(tab.method);
                  setError("");
                }}
              >
                {tab.method === "oneid" ? (
                  <OneIdMark className="oneid-glyph" />
                ) : tab.method === "eimzo" ? (
                  <EImzoMark className="eimzo-glyph" size={18} />
                ) : (
                  <Icon name={tab.icon} size={16} />
                )}
                {t(tab.label)}
              </button>
            ))}
          </div>

          <div aria-live="polite">
            {error ? (
              <div className="alert alert-error" role="alert" style={{ marginBottom: 16 }}>
                <Icon name="alert" size={18} />
                <span>{error}</span>
              </div>
            ) : null}
            {!error && notice && step === "mfa" ? (
              <div className="alert alert-info" style={{ marginBottom: 16 }}>
                <Icon name="info" size={18} />
                <span>{notice}</span>
              </div>
            ) : null}
          </div>

          {method === "email" ? (
            <form className="auth-form" onSubmit={submit} noValidate>
              {step === "credentials" ? (
                <>
                  <div className="field">
                    <label className="label" htmlFor="email">
                      {t("auth.email")}
                    </label>
                    <span className="input-group">
                      <span className="input-icon">
                        <Icon name="mail" size={18} />
                      </span>
                      <input
                        id="email"
                        className="input"
                        type="email"
                        inputMode="email"
                        autoComplete="username"
                        placeholder="ism.familiya@urmon.uz"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        aria-invalid={Boolean(error) && !EMAIL_RE.test(email)}
                        required
                      />
                    </span>
                  </div>

                  <div className="field">
                    <span className="auth-label-row">
                      <label className="label" htmlFor="password">
                        {t("auth.password")}
                      </label>
                      <a href="#recover">{t("auth.forgot")}</a>
                    </span>
                    <span className="input-group has-suffix">
                      <span className="input-icon">
                        <Icon name="lock" size={18} />
                      </span>
                      <input
                        id="password"
                        className="input"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        required
                      />
                      <button
                        type="button"
                        className="input-action"
                        onClick={() => setShowPassword((value) => !value)}
                        aria-label={
                          showPassword ? t("auth.hidePassword") : t("auth.showPassword")
                        }
                      >
                        <Icon name={showPassword ? "eyeOff" : "eye"} size={18} />
                      </button>
                    </span>
                  </div>

                  <div className="auth-row">
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked={trustDevice}
                        onChange={(event) => setTrustDevice(event.target.checked)}
                      />
                      {t("auth.trustDevice")}
                    </label>
                  </div>
                </>
              ) : (
                <div className="mfa-step">
                  <div className="field">
                    <label className="label" htmlFor="otp">
                      {t("auth.mfa.label")}
                    </label>
                    <input
                      id="otp"
                      className="input otp-input"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder="000000"
                      value={otp}
                      onChange={(event) => setOtp(event.target.value.replace(/\D/g, ""))}
                      autoFocus
                      required
                    />
                  </div>
                  <div className="otp-meta">
                    <span>{email}</span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        setStep("credentials");
                        setOtp("");
                        setNotice("");
                      }}
                    >
                      {t("auth.mfa.back")}
                    </button>
                  </div>
                </div>
              )}

              <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={busy}>
                {busy ? t("auth.submitting") : t("auth.submit")}
                {busy ? null : <Icon name="arrowRight" size={18} />}
              </button>
              <Link className="btn btn-outline btn-block" to="/hudud/kirish">
                <Icon name="building" size={16} />
                {tr("Hududiy boshqarma sifatida kirish")}
              </Link>
            </form>
          ) : method === "eimzo" ? (
            <EriLogin next={next} />
          ) : method === "token" ? (
            <UsbTokenLogin next={next} />
          ) : (
            <div className="oneid-card">
              <div className="oneid-head">
                <span className="oneid-logo">
                  <OneIdMark />
                </span>
                <div>
                  <b>{t("auth.oneid.title")}</b>
                  <span>{t("auth.oneid.sub")}</span>
                </div>
              </div>

              <ul className="oneid-list">
                <li>
                  <Icon name="check" size={16} />
                  <span>Shaxs ma'lumotlari davlat reyestridan tasdiqlanadi (PINFL, JShShIR)</span>
                </li>
                <li>
                  <Icon name="check" size={16} />
                  <span>Alohida parol talab qilinmaydi — hisob my.gov.uz bilan bir xil</span>
                </li>
                <li>
                  <Icon name="check" size={16} />
                  <span>Yuridik shaxs nomidan vakolat avtomatik tekshiriladi</span>
                </li>
              </ul>

              <button
                type="button"
                className="btn btn-primary btn-lg btn-block"
                onClick={startOneId}
                disabled={busy}
              >
                {busy ? t("auth.submitting") : t("auth.oneid.button")}
                {busy ? null : <Icon name="external" size={18} />}
              </button>

              <p className="hint">
                Kalitlar sozlanmagan konturda OneID «sandbox» rejimida ishlaydi va sinov
                foydalanuvchisini qaytaradi.
              </p>
            </div>
          )}
        </div>

        <p className="auth-panel-foot">
          <Icon name="shield" size={14} /> {t("auth.foot")}
        </p>
      </section>
    </div>
  );
}
